<template>
  <section :class="[$style.product_list, PRODUCT_LIST_CLASS]">
    <div :class="$style.product_list_container">
      <ProductFilterV2
        :is-open-filter-sp="isOpenFilterSp"
        :sort="localProductSearchQueryVars.sort ?? ProductAllSearchInput_Sort.Recommendation"
        :all-count="productListData?.pageInfo.allCount ?? 0"
        :categories="productCategories"
        :filter="localProductSearchQueryVars"
        :filter-counts="productListData?.filterCounts ?? {}"
        is-link-category
        @update:sort="updateFilterWithReFetch({ value: { sort: $event } })"
        @update:filter="updateFilterWithReFetch({ value: $event })"
        @reset="updateFilterWithReFetch({ value: {}, overwrittenByValue: true })"
        @update:is-open-filter-sp="isOpenFilterSp = $event"
      />
      <div :class="$style.main_contents">
        <div ref="productListRef" :class="$style.products_box">
          <div :class="$style.sort_wrapper">
            <div v-if="previousSearchResult.allCount">
              <p :class="$style.items_count">
                全 {{ previousSearchResult.allCount }} 件中
                {{ `${previousSearchResult.pageStart}〜${previousSearchResult.pageEnd}` }} 件を表示
              </p>
            </div>
            <div :class="$style.products_order">
              <AtomsConsumerButton :class="$style.order_button" @click="isSelectOrderOpen = !isSelectOrderOpen">{{
                ProductAllSearchInput_SortItems[
                  localProductSearchQueryVars.sort ?? ProductAllSearchInput_Sort.Recommendation
                ]
              }}</AtomsConsumerButton>
              <div v-show="isSelectOrderOpen" :class="$style.order_accordion">
                <AtomsConsumerRadioButton
                  v-for="[sortKey, sortLabel] in Object.entries(ProductAllSearchInput_SortItems)"
                  :key="sortKey"
                  :checked="isSelectOrderOpen"
                  name="sort"
                  :value="sortKey"
                  @change="updateFilterWithReFetch({ value: { sort: sortKey as ProductAllSearchInput_Sort } })"
                  @click="isSelectOrderOpen = false"
                >
                  {{ sortLabel }}
                </AtomsConsumerRadioButton>
              </div>
              <div v-if="isSelectOrderOpen" :class="$style.overlay" @click="isSelectOrderOpen = !isSelectOrderOpen" />
            </div>
          </div>

          <ProductQuickFilterV2
            v-if="!md && !isCategorySlugRecoveryWear"
            :categories="productCategories"
            :filter="localProductSearchQueryVars"
            :filter-counts="productListData?.filterCounts ?? {}"
            is-link-category
            @update:filter="updateFilterWithReFetch({ value: $event })"
            @update:open-filter="isOpenFilterSp = $event"
          />

          <ProductListTabBar
            v-if="isCategorySlugRecoveryWear"
            :value="tabBarValue"
            :display-type="tabBarDisplayType"
            :list="[
              { key: 'all', value: 'ALL' },
              { key: 'scene', value: 'シーン別で探す' },
            ]"
            @update:on-click-tab="updateTabValue"
          />

          <ProductFixFilterBar
            v-if="!md && tabBarValue === 'all' && isCategorySlugRecoveryWear"
            :filter="localProductSearchQueryVars"
            @update:is-open-filter-sp="isOpenFilterSp = $event"
          />

          <div v-if="tabBarValue === 'scene'" :class="$style.category_list">
            <ProductCategoryIndexList
              :filter="localProductSearchQueryVars"
              @update:filter="updateCategoryIndexListFilter"
            />
          </div>

          <template v-else>
            <div v-if="productListData?.products?.length ?? 0 > 0">
              <div :class="$style.list_wrap">
                <PagePartsProductListItemV2
                  v-for="product in productListData?.products"
                  :key="product.document_id"
                  :product="product"
                  @open-fav-modal="handleOpenFavModal($event, product)"
                />
              </div>
            </div>
            <div v-if="productListData?.pageInfo.pageCount" :class="$style.pager_wrap">
              <MoleculesConsumerPager
                :total-page="productListData?.pageInfo.pageCount ?? 0"
                :current-page="localProductSearchQueryVars.page"
                :update-page-mode="'emit'"
                @update-page="(page) => refetchWithUpdateQuery(page.toString())"
              />
            </div>
            <div v-else :class="$style.notfound_container">
              <p :class="$style.notfound">条件に合う商品がありません。<br />絞り込みを変更して再度検索してください。</p>
              <AtomsConsumerButton
                v-if="isCategorySlugRecoveryWear"
                type="button"
                :class="$style.backButton"
                @click="updateTabValue('scene')"
              >
                シーン別で探す
              </AtomsConsumerButton>
            </div>
          </template>
        </div>
      </div>
    </div>
    <MoleculesConsumerModal v-model="isOpenFavModal">
      <ProductSelectFav
        :product="favModalProduct"
        :favorite-list="favoriteListRef"
        :is-processing="isProcessing"
        @on-favorite="onFavoriteByProductList"
        @lift="() => (isOpenFavModal = false)"
      />
    </MoleculesConsumerModal>
  </section>
</template>

<script setup lang="ts">
import { LocationQueryValue } from "vue-router"
import { PagePartsFragmentFragment } from "~/types/pageParts"
import { ProductAllSearchInput_Sort, ProductAllSearchInput_SortItems, PRODUCT_LIST_CLASS } from "~/const/product"
import type {
  ProductAllListProps,
  ProductAllListProps_Filter,
  ProductFilterV2Props,
  ProductTabBarMode,
  ProductTabBarType,
} from "~/types/product"
import { usePublicSkuFindManyQuery } from "~/gql/urql.generated"
import type { PublicSkuFindManyQueryVariables } from "~/types/type.generated"
import { useToast } from "~/state"
import type {
  ApiProductSearchQuery,
  ApiProductSearchResponse,
  ApiProductSearchResponse_Product,
} from "~/types/server/api/product"

const props = defineProps<{
  pagePartsCategory: PagePartsFragmentFragment["category"]
  productCategories: ProductFilterV2Props["categories"]
  productListData?: ApiProductSearchResponse
  productSearchQueryVars: ApiProductSearchQuery
}>()

const localProductSearchQueryVars = computed({
  get: () => props.productSearchQueryVars,
  set: (value) => emit("setProductSearchQueryVars", value),
})

const emit = defineEmits<{
  (event: "fetchedProductSearchQuery"): void
  (event: "setProductSearchQueryVars", value: ApiProductSearchQuery): void
}>()

const { openFavModal, onFavoriteByProductList, isOpenFavModal, favModalProduct, favoriteListRef, isProcessing } =
  useFavorite()

const isOpenFilterSp = ref(false)
const { width: windowWidth } = useWindowSize()
const md = computed(() => windowWidth.value >= 768)

const route = useRoute()
const router = useRouter()
const isSelectOrderOpen = ref<boolean>(false)

/**
 * リカバリーウェアのカテゴリーかどうか
 * - シーン別タブを表示するかどうかの判定に使用
 */
const isCategorySlugRecoveryWear =
  (route.params.subCategorySlug ? route.params.subCategorySlug.toString() : route.params.categorySlug.toString()) ===
  "recovery-wear"

const tabBarValue = ref<ProductTabBarMode>(
  !isCategorySlugRecoveryWear ? "all" : route.query.tab_value === "scene" ? "scene" : "all",
)
const tabBarDisplayType = ref<ProductTabBarType>(route.query.tab_type === "back" ? "back" : "tab")

const { PRODUCT_LIST_LIMIT } = useProductsPage()

const publicSkuFindManyQueryVariables = ref<PublicSkuFindManyQueryVariables>({})
const { executeQuery: publicSkuFindManyQueryExec, resume: publicSkuFindManyQueryResume } = usePublicSkuFindManyQuery({
  requestPolicy: "network-only",
  variables: publicSkuFindManyQueryVariables,
  pause: true,
})

const toast = useToast()
const handleOpenFavModal = async (e: Event, product: ApiProductSearchResponse_Product) => {
  e.preventDefault()

  publicSkuFindManyQueryVariables.value = {
    filter: {
      product_id: product.document_id,
      is_status: true,
    },
  }
  await publicSkuFindManyQueryResume()
  const { data, error } = await publicSkuFindManyQueryExec()
  if (!data.value?.publicSkuFindMany.length || error.value) {
    toast?.showErrorToasted("お気に入り情報の取得に失敗しました。再度お試しください。")
    return
  }

  openFavModal(e, {
    document_id: product.document_id,
    name: product.name,
    material: product.material,
    skus: data.value.publicSkuFindMany
      .sort((a, b) => a.display_order_number - b.display_order_number)
      .map((sku) => ({
        document_id: sku.document_id,
        color: sku.color,
        size: sku.size,
        price: sku.price,
        main_image: sku.main_image,
      })),
  })
}

const productListRef = ref<HTMLElement>()
const refetchWithUpdateQuery = async (page?: string) => {
  await navigateTo({
    query: {
      keywords: Array.isArray(localProductSearchQueryVars.value.keywords)
        ? localProductSearchQueryVars.value.keywords.join(",")
        : localProductSearchQueryVars.value.keywords,
      category: localProductSearchQueryVars.value.category,
      sex: Array.isArray(localProductSearchQueryVars.value.sex)
        ? localProductSearchQueryVars.value.sex.join(",")
        : localProductSearchQueryVars.value.sex,
      fabric: Array.isArray(localProductSearchQueryVars.value.fabric)
        ? localProductSearchQueryVars.value.fabric.join(",")
        : localProductSearchQueryVars.value.fabric,
      season: Array.isArray(localProductSearchQueryVars.value.season)
        ? localProductSearchQueryVars.value.season.join(",")
        : localProductSearchQueryVars.value.season,
      wear_category: Array.isArray(localProductSearchQueryVars.value.wear_category)
        ? localProductSearchQueryVars.value.wear_category.join(",")
        : localProductSearchQueryVars.value.wear_category,
      size: Array.isArray(localProductSearchQueryVars.value.size)
        ? localProductSearchQueryVars.value.size.join(",")
        : localProductSearchQueryVars.value.size,
      silhouette: Array.isArray(localProductSearchQueryVars.value.silhouette)
        ? localProductSearchQueryVars.value.silhouette.join(",")
        : localProductSearchQueryVars.value.silhouette,
      ...(localProductSearchQueryVars.value.is_in_stock === "1" ? { is_in_stock: "1" } : {}),
      ...(localProductSearchQueryVars.value.is_outlet === "1" ? { is_outlet: "1" } : {}),
      ...(localProductSearchQueryVars.value.campaign ? { campaign: localProductSearchQueryVars.value.campaign } : {}),
      page: page ?? route.query.page?.toString() ?? "1",
      limit: PRODUCT_LIST_LIMIT,
      sort: localProductSearchQueryVars.value.sort,
      tab_value: route.query.tab_value,
      tab_type: route.query.tab_type,
    },
  })
  emit("fetchedProductSearchQuery")
  productListRef.value?.scrollIntoView({ behavior: "smooth", block: "start" })
}

const updateFilterWithReFetch = async (args: {
  value: ProductAllListProps_Filter
  /** true: 上書き、false|undefined: 上書きしない（更新する） */
  overwrittenByValue?: boolean
}) => {
  if (args.overwrittenByValue) {
    localProductSearchQueryVars.value.category = args.value.category ?? ""
    localProductSearchQueryVars.value.sex = args.value.sex ?? []
    localProductSearchQueryVars.value.fabric = args.value.fabric ?? []
    localProductSearchQueryVars.value.season = args.value.season ?? []
    localProductSearchQueryVars.value.wear_category = args.value.wear_category ?? []
    localProductSearchQueryVars.value.size = args.value.size ?? []
    localProductSearchQueryVars.value.is_in_stock = args.value.is_in_stock ?? "0"
    localProductSearchQueryVars.value.is_outlet = args.value.is_outlet ?? "0"
    localProductSearchQueryVars.value.sort = args.value.sort ?? ProductAllSearchInput_Sort.Recommendation
    localProductSearchQueryVars.value.silhouette = args.value.silhouette ?? []
  } else {
    localProductSearchQueryVars.value.category = args.value.category ?? localProductSearchQueryVars.value.category
    localProductSearchQueryVars.value.sex = args.value.sex ?? localProductSearchQueryVars.value.sex
    localProductSearchQueryVars.value.fabric = args.value.fabric ?? localProductSearchQueryVars.value.fabric
    localProductSearchQueryVars.value.season = args.value.season ?? localProductSearchQueryVars.value.season
    localProductSearchQueryVars.value.wear_category =
      args.value.wear_category ?? localProductSearchQueryVars.value.wear_category
    localProductSearchQueryVars.value.size = args.value.size ?? localProductSearchQueryVars.value.size
    localProductSearchQueryVars.value.is_in_stock =
      args.value.is_in_stock ?? localProductSearchQueryVars.value.is_in_stock
    localProductSearchQueryVars.value.is_outlet = args.value.is_outlet ?? localProductSearchQueryVars.value.is_outlet
    localProductSearchQueryVars.value.sort = args.value.sort ?? localProductSearchQueryVars.value.sort
    localProductSearchQueryVars.value.silhouette = args.value.silhouette ?? localProductSearchQueryVars.value.silhouette
  }
  await refetchWithUpdateQuery("1")
}

const previousSearchResult = computed<Omit<ProductAllListProps["previousSearchResult"], "keyword">>(() => {
  const allCount = props.productListData?.pageInfo.allCount ?? 0
  const startZero = Number(PRODUCT_LIST_LIMIT) * (Number(localProductSearchQueryVars.value.page) - 1)
  const end = startZero + Number(PRODUCT_LIST_LIMIT)

  return {
    allCount,
    pageStart: startZero + 1,
    pageEnd: end > allCount ? allCount : end,
  }
})

const updateTabValue = (value: ProductTabBarMode, displayType?: ProductTabBarType) => {
  tabBarValue.value = !isCategorySlugRecoveryWear ? "all" : value === "scene" ? "scene" : "all"
  tabBarDisplayType.value = displayType ?? "tab"
  const currentQuery: { [x: string]: LocationQueryValue | LocationQueryValue[] } = {
    ...route.query,
    tab_type: tabBarDisplayType.value,
  }
  if (tabBarValue.value === "scene") {
    router.push({ query: { ...currentQuery, tab_value: "scene" } })
  } else if (currentQuery.tab_value === "scene") {
    const { tab_value, ...rest } = currentQuery
    if (!tab_value) return
    router.push({ query: { ...rest } })
  }
}

const updateCategoryIndexListFilter = async (query: ApiProductSearchQuery, tabValue: ProductTabBarMode) => {
  await updateFilterWithReFetch({ value: query, overwrittenByValue: true })
  updateTabValue(tabValue, "back")
}

watch(
  () => route.query,
  () => {
    tabBarValue.value = !isCategorySlugRecoveryWear ? "all" : route.query.tab_value === "scene" ? "scene" : "all"
    tabBarDisplayType.value = route.query.tab_type === "back" ? "back" : "tab"
  },
  {
    deep: true,
  },
)
</script>

<style lang="scss" module scoped>
.product_list {
  .notfound_container {
    margin: 6rem 0.5rem 4rem;
    .notfound {
      text-align: center;
    }

    button {
      width: 60%;
      display: block;
      margin: 3rem auto 0;
      padding: 1rem 0.7rem;
      background: $btn-bg-primary;
      color: $white;
      border-radius: 6px;
    }
  }
  .product_list_container {
    display: flex;
    justify-content: center;
    padding: 0;
    @include md {
      padding: 0 1rem;
    }
    .main_contents {
      flex-grow: 1;
      width: 100%;
      max-width: $max-width-md;

      .products_box {
        .sort_wrapper {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1rem 0.5rem;
          background-color: $white;
          border-top: 1px solid $primary-20;
          border-bottom: 1px solid $primary-20;

          @include md {
            border: none;
          }

          .items_count {
            color: $text-gray;
            line-height: 1.25rem;
            font-size: $font-size-14;
          }

          .products_order {
            position: relative;
            font-size: $font-size-14;
            font-weight: bold;
            min-width: 6.5rem;

            .order_button {
              @include sub-font;
              position: relative;
              padding: 0 0 0 1.5rem;
              font-size: $font-size-13;
              color: $black;
              cursor: pointer;
              text-decoration: underline;

              &:hover {
                opacity: 0.7;
              }

              &:after {
                position: absolute;
                top: 50%;
                left: 0;
                width: 14px;
                height: 16px;
                content: "";
                background: url("/images/common/arrow/sort.svg");
                background-size: contain;
                transform: translateY(-50%);
              }
            }

            .order_accordion {
              position: absolute;
              top: 2rem;
              right: -0.5rem;
              z-index: 10;
              width: 100vw;
              padding: 1rem;
              background-color: $white;
              filter: drop-shadow(0 0.25rem 0.25rem rgba($black, 0.1));
              @include md {
                right: 0;
                width: 20rem;
              }
              :global {
                label {
                  padding: 0.5rem;
                  border-bottom: 1px solid $primary-20;
                  flex-direction: row-reverse;
                  justify-content: space-between;
                  &:last-child {
                    border-bottom: none;
                  }
                  input {
                    border: none;
                    &::after,
                    &::before:not(:checked) {
                      display: none;
                    }
                    // チェックアイコン
                    &:checked {
                      font-weight: bold;
                      &::before {
                        top: calc(50% - 0.375rem);
                        right: 0;
                        left: auto;
                        transform: rotate(50deg);
                        width: 0.45rem;
                        height: 0.75rem;
                        border-radius: 0;
                        background: none;
                        border-right: 2px solid $secondary;
                        border-bottom: 2px solid $secondary;
                        content: "";
                      }
                    }
                  }
                }
              }
            }

            .overlay {
              position: fixed;
              inset: 0px;
            }
          }
        }
        .list_wrap {
          display: grid;
          grid-template-columns: repeat(auto-fit, calc(100% / 2 - (0.5rem / 2)));
          column-gap: 0.5rem;
          row-gap: 1rem;
          margin: 1rem 0;
          padding: 0 0.5rem;
          @include lg {
            grid-template-columns: repeat(auto-fit, calc(100% / 3 - (1rem / 3 * 2)));
            column-gap: 1rem;
            row-gap: 1.5rem;
            margin-bottom: 2rem 0 5rem;
            padding: 0;
          }
        }
      }
    }

    .pager_wrap {
      display: flex;
      justify-content: flex-end;
      margin-right: 1rem;
    }
  }
}
</style>
